// Umami loader. Idempotent: calling load() twice is safe.
//
// Unlike GA4/GTM, Umami is COOKIELESS and ANONYMOUS by design - it sets no
// cookies, stores no persistent visitor id, and retains no personal data
// (unique visitors are a daily-rotating salted hash, never stored raw). Because
// it neither reads nor writes the user's device, the ePrivacy / GDPR Art. 88a
// consent trigger does not fire, so it loads PRE-CONSENT for 100% of traffic -
// it is intentionally NOT behind the consent gate that wraps GA4/GTM.
//
// Hosting is Umami Cloud on the EU region (the workspace is EU-resident, so the
// data stays in the EU). The Cloud script + ingest host is cloud.umami.is for
// all Cloud customers - region routing happens server-side, not via the
// hostname. Disclose it in the privacy/cookie policy as an anonymous processor.

const DEFAULT_UMAMI_WEBSITE_ID = '08b07756-4eb3-41cf-9a21-1c58be931adc'
const DEFAULT_UMAMI_SRC = 'https://cloud.umami.is/script.js'
const SCRIPT_ATTR = 'data-helin-umami'

export function getUmamiWebsiteId(): string {
  const fromEnv = (
    import.meta.env.VITE_UMAMI_WEBSITE_ID as string | undefined
  )?.trim()
  return fromEnv && fromEnv.length > 0 ? fromEnv : DEFAULT_UMAMI_WEBSITE_ID
}

export function getUmamiSrc(): string {
  const fromEnv = (import.meta.env.VITE_UMAMI_SRC as string | undefined)?.trim()
  return fromEnv && fromEnv.length > 0 ? fromEnv : DEFAULT_UMAMI_SRC
}

export function isUmamiLoaded(): boolean {
  return document.querySelector(`script[${SCRIPT_ATTR}]`) !== null
}

export function loadUmami(
  websiteId: string = getUmamiWebsiteId(),
  src: string = getUmamiSrc(),
): void {
  if (isUmamiLoaded()) return
  if (!websiteId) return

  const script = document.createElement('script')
  // Umami ships `defer` (not async) so the queue of in-page events stays
  // ordered relative to the tracker booting - matches the vendor snippet.
  script.defer = true
  script.src = src
  script.setAttribute('data-website-id', websiteId)
  script.setAttribute(SCRIPT_ATTR, websiteId)
  document.head.appendChild(script)
}
